import { useIntl } from 'react-intl'
import makeStyles from '@mui/styles/makeStyles'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { convertHcpToString } from 'utils/convertHcpToString'
import { getCurrentUTCDateTime } from 'utils/formatDate'
import { Box, Typography } from '@mui/material'

const useStyles = makeStyles(() => ({
  pageHeader: {
    display: 'flex',
    height: '170px',
    width: '100%',
    backgroundColor: '#047448',
    borderRadius: 16,
    marginLeft: 20,
    marginRight: 20,
  },
  playerInfoContainer: {
    display: 'flex',
    height: '100%',
    width: '100%',
    fontFamily: 'Söhne',
  },
  playerInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    alignContent: 'flex-start',
    padding: 20,
  },
  playerImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 20,
  },
  playerImage: {
    width: '115px',
    height: '115px',
    borderRadius: '50%',
    border: '3px solid #fff',
    backgroundColor: '#fff',
    marginTop: '-10px',
  },
  playerImageGold: {
    width: '115px',
    height: '115px',
    borderRadius: '50%',
    border: '3px solid #ffd700',
    backgroundColor: '#fff',
    marginTop: '-10px',
  },
  playerStatsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: '10px',

    '& > div': {
      width: '33.33%',
      marginBottom: '5px',
      fontSize: '13px',
      lineHeight: '14px',
      color: '#ececec',

      '& > span': {
        fontWeight: 600,
        marginLeft: '8px',
      },
    },
  },
  playerName: {
    height: 40,
    fontSize: '30px',
    fontWeight: 600,
    color: '#fff',
    flexBasis: '100%',
    margin: '10px 0 5px',
  },
  playerDetailsContainer: {
    display: 'flex',
    width: '100%',
    '& span': {
      height: '24px',
      fontSize: 14,
      fontWeight: 500,
      color: '#ececec',
    },
    '& span:not(:first-of-type), span:not(:last-of-type)': {
      marginRight: 10,
    },
  },
}))

const PageHeader = ({ userInfo }: { userInfo: UserState }) => {
  const classes = useStyles()
  const intl = useIntl()

  const { user, userExtra, userStats, userGameStats, status, userExtraStatus, userStatsStatus, userGameStatsStatus } =
    userInfo

  const isGoldUser = () => {
    const goldMemberships = user.privileges?.filter((p: UserPrivilege) => {
      const validFrom = p.validFrom?.date ? p.validFrom.date : p.validFrom
      const isStarted = validFrom ? new Date(getCurrentUTCDateTime()) >= new Date(validFrom) : true

      const validTo = p.validTo?.date ? p.validTo.date : p.validTo
      const isNotFinished = validTo ? new Date(validTo) > new Date(getCurrentUTCDateTime()) : true

      const isActive = isStarted && isNotFinished

      return p.name === 'GOLD_MEMBERSHIP' && isActive
    })
    return goldMemberships && goldMemberships.length > 0
  }

  const getAllTimeBest = () => {
    const stats = userStats.filter((stats: UserStats) => stats.type === 'all')
    return stats.length > 0 ? stats[0].strokesLow : '-'
  }

  const getSeasonBest = () => {
    const currentYear = new Date().getFullYear()
    const stats = userStats.filter(
      (stats: UserStats) => stats.type === 'season' && stats.group && parseInt(stats.group) === currentYear,
    )
    return stats.length > 0 ? stats[0].strokesLow : '-'
  }

  const getAvgScore = () => {
    const stats = userStats.filter((stats: UserStats) => stats.type === 'all')
    return stats.length > 0 ? stats[0].strokesPerRound : '-'
  }

  if (status === 'loading') {
    return (
      <Box className={classes.pageHeader} display="flex" justifyContent="center" alignItems="center">
        <LoadingSpinner size={75} />
      </Box>
    )
  }

  return (
    <Box className={classes.pageHeader}>
      <Box className={classes.playerInfoContainer}>
        <Box className={classes.playerImageContainer}>
          {status === 'succeeded' &&
            (isGoldUser() ? (
              <img className={classes.playerImageGold} src={user.avatarUrl} />
            ) : (
              <img className={classes.playerImage} src={user.avatarUrl} />
            ))}
        </Box>
        <Box className={classes.playerInfo}>
          <Typography className={classes.playerName} component={'h2'}>
            {user.fullName}
          </Typography>

          <Box className={classes.playerDetailsContainer}>
            {user.handicap && (
              <>
                <span>HCP {convertHcpToString(user.handicap)}</span>
                <span>|</span>
              </>
            )}
            {user.club?.name && (
              <>
                <span>{user.club?.name}</span>
                <span>|</span>
              </>
            )}
            {user.country?.name && <span>{user.country?.name}</span>}
          </Box>
          <Box className={classes.playerStatsContainer}>
            <div>
              {intl.formatMessage({ id: 'strings.rounds' })}
              {userGameStatsStatus === 'loading' ? (
                <LoadingSpinner size={12} />
              ) : (
                <span>{userGameStats?.totalRoundsPlayed}</span>
              )}
            </div>
            <div>
              {intl.formatMessage({ id: 'strings.friends' })}{' '}
              {userExtraStatus === 'loading' ? <LoadingSpinner size={12} /> : <span>{userExtra?.friendsCount}</span>}
            </div>
            <div>
              {intl.formatMessage({ id: 'strings.following' })}
              {userExtraStatus === 'loading' ? <LoadingSpinner size={12} /> : <span>{userExtra?.followingCount}</span>}
            </div>
            <div>
              {intl.formatMessage({ id: 'strings.allTimeBest' })}
              {userStatsStatus === 'loading' ? <LoadingSpinner size={12} /> : <span>{getAllTimeBest()}</span>}
            </div>
            <div>
              {intl.formatMessage({ id: 'strings.seasonBest' })}
              {userStatsStatus === 'loading' ? <LoadingSpinner size={12} /> : <span>{getSeasonBest()}</span>}
            </div>
            <div>
              {intl.formatMessage({ id: 'strings.avgScore' })}
              {userStatsStatus === 'loading' ? <LoadingSpinner size={12} /> : <span>{getAvgScore()}</span>}
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PageHeader
